import { Fragment, type ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';
import cn from 'classnames';

import { tr } from '@/video/translations';
import { VideoPlayer } from '@/video/VideoPlayer';

import { COMMUNITY_NODE_CLASS_NAMES } from '../utils/communityContent/communityNodeStyle';
import { isMentionAvailable, MENTION_FALLBACK_LABEL, mentionDisplayLabel } from '../utils/mentions/mentionDisplay';
import {
  MENTION_CLASS_NAME,
  MENTION_LINK_CLASS_NAME,
  MENTION_UNAVAILABLE_CLASS_NAME,
} from '../utils/mentions/mentionStyle';
import type { MentionType as StoredMentionType } from '../utils/mentions/mentionType';

import { LinkEmbedRenderer } from '../communityPostComposer/_components/nodes/LinkEmbed/LinkEmbedRenderer';
import { resolveEmojiName } from '../communityPostComposer/resolveEmojiName';
import type { VideoPlaybackResolver, VideoStatusSubscriber } from '../types';

// Hosts that render mentions outside Tiptap, like chat messages, reuse the chip styles.
export { MENTION_CLASS_NAME, MENTION_LINK_CLASS_NAME, MENTION_UNAVAILABLE_CLASS_NAME };
export type MentionType = StoredMentionType;

export const COMMUNITY_IMAGE_WIDTH = 1300;
export const COMMUNITY_IMAGE_CLASS_NAME = 'max-w-full h-auto rounded-lg my-3';

export const hasTiptapContent = (tiptapState?: JSONContent | null): boolean => !!tiptapState?.content?.length;

const UNSAFE_URL_PATTERN = /^(javascript|data|vbscript):/i;

export const sanitizeHref = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (UNSAFE_URL_PATTERN.test(url)) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const safeHostname = (href: string): string => {
  try {
    return new URL(href).hostname;
  } catch {
    return href;
  }
};

// Host apps rewrite image URLs through their own CDN; default to identity when omitted.
export type ImageSrcTransform = (src: string, width: number) => string;

export interface MentionLinkProps {
  type: MentionType;
  id: string;
  className: string;
  children: ReactNode;
}

export interface SerializerOptions {
  imageSrc: ImageSrcTransform;
  // Receives the stored URL, before imageSrc is applied.
  renderImage?: (image: { src: string; alt: string }) => ReactNode;
  // When present, video nodes render a player, which waits while the video is still
  // processing; without it they fall back to a neutral placeholder so hosts that
  // don't support playback still render.
  videoPlayback?: VideoPlaybackResolver;
  // Lets a processing video start as soon as it is ready instead of on the next poll.
  videoStatus?: VideoStatusSubscriber;
  videoTranslations?: Record<string, string>;
  // The host owns the mention link (its url and router), which differs between Orchid and Swarm.
  // Without it, a mention renders as a span.
  renderMentionLink?: (link: MentionLinkProps) => ReactNode;
  // Shown for a mention whose member is gone, which the API sends with a null label.
  mentionFallbackLabel?: string;
}

export const serializeNode = (node: JSONContent, key: string, options: SerializerOptions): ReactNode => {
  if ('text' in node && node.text !== undefined) {
    return <Fragment key={key}>{renderTextLeaf(node)}</Fragment>;
  }

  const children = node.content?.map((child, i) => serializeNode(child, `${key}-${i}`, options)) ?? [];

  return getElement(node, key, children, options);
};

const getElement = (node: JSONContent, key: string, children: ReactNode[], options: SerializerOptions): ReactNode => {
  const {
    imageSrc,
    videoPlayback,
    videoStatus,
    videoTranslations,
    renderImage,
    renderMentionLink = renderMentionAsSpan,
    mentionFallbackLabel = MENTION_FALLBACK_LABEL,
  } = options;

  switch (node.type) {
    case 'doc':
      return <Fragment key={key}>{children}</Fragment>;
    case 'paragraph':
      return (
        <p key={key} className="mb-4 last:mb-0">
          {children}
        </p>
      );
    case 'heading': {
      const level = Math.min(6, Math.max(1, Number(node.attrs?.level) || 2));
      const Tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return (
        <Tag key={key} className={COMMUNITY_NODE_CLASS_NAMES.heading}>
          {children}
        </Tag>
      );
    }
    case 'bulletList':
      return (
        <ul key={key} className={COMMUNITY_NODE_CLASS_NAMES.bulletList}>
          {children}
        </ul>
      );
    case 'orderedList':
      return (
        <ol
          key={key}
          className={COMMUNITY_NODE_CLASS_NAMES.orderedList}
          start={node.attrs?.start as number | undefined}
        >
          {children}
        </ol>
      );
    case 'listItem':
      return (
        <li key={key} className={COMMUNITY_NODE_CLASS_NAMES.listItem}>
          {children}
        </li>
      );
    case 'blockquote':
      return (
        <blockquote key={key} className={COMMUNITY_NODE_CLASS_NAMES.blockquote}>
          {children}
        </blockquote>
      );
    case 'emoji': {
      const name = typeof node.attrs?.name === 'string' ? node.attrs.name : undefined;
      if (!name) return null;
      const emoji = resolveEmojiName(name);
      return emoji ? (
        <span key={key} role="img" aria-label={name}>
          {emoji}
        </span>
      ) : (
        <Fragment key={key}>:{name}:</Fragment>
      );
    }
    case 'mention': {
      const attributes = node.attrs ?? {};
      const id = typeof attributes.id === 'string' ? attributes.id : undefined;
      if (!id) return null;
      if (!isMentionAvailable(attributes)) {
        return (
          <span key={key} className={MENTION_UNAVAILABLE_CLASS_NAME}>
            {mentionDisplayLabel(attributes, mentionFallbackLabel)}
          </span>
        );
      }
      const className = cn(MENTION_CLASS_NAME, MENTION_LINK_CLASS_NAME);
      return (
        <Fragment key={key}>
          {renderMentionLink({ type: attributes.type, id, className, children: attributes.label })}
        </Fragment>
      );
    }
    case 'hardBreak':
      return <br key={key} />;
    case 'image': {
      const src = sanitizeHref(node.attrs?.src as string | undefined);
      if (!src) return null;
      const alt = (node.attrs?.alt as string | undefined) ?? '';
      if (renderImage) return <Fragment key={key}>{renderImage({ src, alt })}</Fragment>;
      return (
        <img
          key={key}
          src={imageSrc(src, COMMUNITY_IMAGE_WIDTH)}
          alt={alt}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className={COMMUNITY_IMAGE_CLASS_NAME}
        />
      );
    }
    case 'linkCard': {
      const href = sanitizeHref(node.attrs?.url as string | undefined);
      if (!href) return null;
      const image = sanitizeHref(node.attrs?.image as string | undefined);
      const title = (node.attrs?.title as string | undefined) ?? '';
      const description = (node.attrs?.description as string | undefined) ?? '';
      const siteName = (node.attrs?.siteName as string | undefined) ?? safeHostname(href);
      const faviconSrc = image ? '' : `https://${safeHostname(href)}/favicon.ico`;
      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="my-3 flex overflow-hidden rounded-xl border border-black/10 bg-white no-underline"
        >
          {image && (
            <div className="w-[88px] min-h-[88px] shrink-0 overflow-hidden bg-black/5">
              <img
                src={imageSrc(image, 256)}
                alt=""
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className="size-full object-cover"
              />
            </div>
          )}
          <div className="flex min-w-0 flex-col justify-center gap-0.5 p-3">
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-black/50">
              {faviconSrc && (
                <img
                  src={faviconSrc}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  className="size-4 shrink-0 rounded-sm"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <span className="truncate">{siteName}</span>
            </span>
            {title && <span className="truncate text-sm font-semibold text-black/90">{title}</span>}
            {description && <span className="line-clamp-2 text-xs text-black/60">{description}</span>}
          </div>
        </a>
      );
    }
    case 'linkEmbed': {
      const service = node.attrs?.service as string | undefined;
      const data = (node.attrs?.data as Record<string, unknown> | null | undefined) ?? null;
      return <LinkEmbedRenderer key={key} service={service} data={data} className="my-3" />;
    }
    case 'video': {
      const videoId = typeof node.attrs?.videoId === 'string' ? node.attrs.videoId : '';
      if (!videoId) return null;
      if (videoPlayback) {
        return (
          <VideoPlayer
            key={key}
            videoId={videoId}
            resolvePlayback={videoPlayback}
            subscribeToStatus={videoStatus}
            translations={videoTranslations}
            className="my-3"
          />
        );
      }
      // Neutral placeholder for hosts that don't wire playback. Inline aspect ratio,
      // because Orchid's Tailwind theme does not generate `aspect-video`.
      return (
        <div
          key={key}
          data-video-id={videoId}
          className="my-3 flex w-full items-center justify-center rounded-lg border border-black/10 bg-black/5 text-sm text-black/50"
          style={{ aspectRatio: '16 / 9' }}
        >
          {tr(videoTranslations, 'video.placeholder')}
        </div>
      );
    }
    default:
      return <Fragment key={key}>{children}</Fragment>;
  }
};

type TiptapMark = NonNullable<JSONContent['marks']>[number];

const renderMarks = (marks: TiptapMark[], content: ReactNode): ReactNode => {
  if (marks.length === 0) return content;

  const [mark, ...rest] = marks;
  const inner = renderMarks(rest, content);

  switch (mark.type) {
    case 'bold':
      return <strong>{inner}</strong>;
    case 'italic':
      return <em>{inner}</em>;
    case 'underline':
      return <u>{inner}</u>;
    case 'strike':
      return <s>{inner}</s>;
    case 'code':
      return <code>{inner}</code>;
    case 'link': {
      const href = sanitizeHref(mark.attrs?.href as string | undefined);
      return href ? (
        <a href={href} className="underline break-words" target="_blank" rel="noopener noreferrer nofollow">
          {inner}
        </a>
      ) : (
        inner
      );
    }
    default:
      return <span>{inner}</span>;
  }
};

const renderTextLeaf = (node: JSONContent): ReactNode => {
  return renderMarks(node.marks ?? [], node.text ?? '');
};

// Hosts without a link for mentions (Swarm) show the chip as text.
const renderMentionAsSpan = ({ className, children }: MentionLinkProps): ReactNode => (
  <span className={className}>{children}</span>
);
